import { IMAGE_URL } from '../api/client';

const PASSWORD_SIGN_IN_PROVIDER = 'password';

type SignInProvider = string[] | string | null | undefined;

function normalizeSignInProvider(signInProvider: SignInProvider): string[] {
  if (Array.isArray(signInProvider)) {
    return signInProvider.map((provider) => String(provider).toLowerCase());
  }

  if (typeof signInProvider === 'string') {
    const trimmed = signInProvider.trim();
    if (!trimmed) {
      return [];
    }
    return [trimmed.toLowerCase()];
  }

  return [];
}

export function hasPasswordSignInProvider(signInProvider: SignInProvider): boolean {
  const providers = normalizeSignInProvider(signInProvider);
  return providers.includes(PASSWORD_SIGN_IN_PROVIDER);
}

export function getUserImageSrc(
  image: unknown,
  _signInProvider: SignInProvider
): string | null {
  const getImageValue = (value: unknown): string | null => {
    if (!value) return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const candidate = getImageValue(item);
        if (candidate) return candidate;
      }
      return null;
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if (typeof obj.url === 'string') {
        const trimmed = obj.url.trim();
        if (trimmed) return trimmed;
      }
      if (typeof obj.image === 'string') {
        const trimmed = obj.image.trim();
        if (trimmed) return trimmed;
      }
    }

    return null;
  };

  const normalizedImage = getImageValue(image);
  if (!normalizedImage) {
    return null;
  }

  if (normalizedImage.startsWith('https://') || normalizedImage.startsWith('http://')) {
    return normalizedImage;
  }

  const isUsersPath =
    normalizedImage.startsWith('/users/') ||
    normalizedImage.startsWith('users/') ||
    normalizedImage.includes('/users/');

  // For customer/provider profile images, only /users/ paths should use IMAGE_URL.
  // Otherwise use the image value as returned by backend.
  if (!isUsersPath) {
    return normalizedImage;
  }

  if (!IMAGE_URL) {
    return normalizedImage;
  }

  const baseUrl = IMAGE_URL.endsWith('/') ? IMAGE_URL.slice(0, -1) : IMAGE_URL;
  const imagePath = normalizedImage.startsWith('/') ? normalizedImage : `/${normalizedImage}`;

  return `${baseUrl}${imagePath}`;
}

export function getUserInitial(name: string | null | undefined, fallback = 'U'): string {
  const value = name?.trim();
  if (!value) {
    return fallback;
  }
  return value.charAt(0).toUpperCase();
}

export function getAvatarBackgroundColor(seed: string | null | undefined): string {
  const normalizedSeed = seed?.trim().toLowerCase();
  if (!normalizedSeed) {
    return 'hsl(210, 60%, 50%)';
  }

  let hash = 0;
  for (let i = 0; i < normalizedSeed.length; i += 1) {
    hash = (hash * 31 + normalizedSeed.charCodeAt(i)) % 360;
  }

  return `hsl(${hash}, 65%, 50%)`;
}
